import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { X, Mail } from "lucide-react";
import { ContactInformation } from "./lead/ContactInformation";
import { EmploymentDetails } from "./lead/EmploymentDetails";
import { BenefitsRetirement } from "./lead/BenefitsRetirement";
import { SeveranceInformation } from "./lead/SeveranceInformation";
import { AdditionalInformation } from "./lead/AdditionalInformation";
import { CampaignInformation } from "./lead/CampaignInformation";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

type Lead = Tables<"leads">;

interface LeadCardProps {
  lead: Lead;
  isOpen: boolean;
  onClose: () => void;
}

const LeadCard = ({ lead, isOpen, onClose }: LeadCardProps) => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const formatArrayField = (arr: string[] | null) => {
    return arr?.length ? arr.join(", ") : "None specified";
  };

  const formatDate = (date: string | null) => {
    return date ? format(new Date(date), "MMMM d, yyyy") : "Not specified";
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    try {
      const { error } = await supabase.functions.invoke('notify-new-lead', {
        body: { 
          record: {
            ...lead,
            healthcare_options: formatArrayField(lead.healthcare_options),
            retirement_accounts: formatArrayField(lead.retirement_accounts),
            employment_status: lead.employment_status?.replace(/_/g, " ") || "Not specified",
            pension_status: lead.pension_status?.replace(/_/g, " ") || "Not specified",
            severance_status: lead.severance_status?.replace(/_/g, " ") || "Not specified",
            preferred_contact_method: lead.preferred_contact_method?.replace(/_/g, " ") || "Not specified",
            last_day: formatDate(lead.last_day),
            severance_deadline: formatDate(lead.severance_deadline),
            created_at: formatDate(lead.created_at),
          },
          to: [emailAddress]
        }
      });

      if (error) throw error;

      toast({
        title: "Email Sent",
        description: `Lead information has been sent to ${emailAddress}`,
      });
      setEmailDialogOpen(false);
      setEmailAddress("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={onClose}>
        <AlertDialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <AlertDialogDescription className="sr-only">
            Lead details and information
          </AlertDialogDescription>
          
          <div className="flex justify-between items-center absolute right-4 top-4 gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setEmailDialogOpen(true)}
            >
              <Mail className="h-4 w-4" />
              <span className="sr-only">Email lead information</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          <AlertDialogHeader>
            <AlertDialogTitle>Lead Details</AlertDialogTitle>
          </AlertDialogHeader>

          <ContactInformation lead={lead} />
          <EmploymentDetails lead={lead} />
          <BenefitsRetirement lead={lead} />
          <SeveranceInformation lead={lead} />
          <AdditionalInformation lead={lead} />
          <CampaignInformation lead={lead} />
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Lead Information</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Enter email address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSending}>
                {isSending ? "Sending..." : "Send"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadCard;